import React, { Component } from 'react';
import { Card, Row, Col, Button, UncontrolledTooltip } from 'reactstrap';
import { FormattedMessage } from 'react-intl';
import Authorization, { CheckAuthorization } from '../Base/Authorization';
import moment from 'moment';
import { KebabMenu } from '../Base/Common/CommonUIComponents';
import { getTranslation } from '../Base/Common/ReferenceDataFunctions';

// resolves the title to show on a voucher card following the chain:
// getTranslation (hotel language, EN, first with a title) -> el.title (neutral) -> '' (show only the code).
// the /api/Voucher/v1/Config list endpoint (VoucherTypeHeader) exposes the per-language array as `languages`
// ({ languageCode, title, description, shortDescription }, title omitted when null) plus a top-level neutral `title`.
// empty/whitespace values are treated as absent so the layout never breaks.
export function resolveVoucherTitle(el) {
    if (!el) return '';
    const localized = getTranslation(el.languages || el.multiLingual, 'title');
    const candidate = (localized && localized.title) || el.title;
    return candidate && candidate.trim() ? candidate.trim() : '';
}

// returns the list of translation ids explaining why this voucher type cannot be emitted/sold (empty if it can)
function getEmitBlockReasons(el, { canEmitVoucher = true, requiresRebates = false } = {}) {
    const reasons = [];

    if (!el.active) {
        reasons.push("VoucherCardDetail.ReasonInactive");
    }
    if (!(el.sold < el.maxQuantity)) {
        reasons.push("VoucherCardDetail.ReasonSoldOut");
    }
    if (moment().diff(moment(el.saleValidTo), 'days') > 0) {
        reasons.push("VoucherCardDetail.ReasonSalePeriodEnded");
    }
    if (el.channels.find(c => c.channelConfigId === 2) === undefined) {
        reasons.push("VoucherCardDetail.ChannelNotAvailable");
    }
    if (!el.creationValidHotels || el.creationValidHotels.length === 0) {
        reasons.push("VoucherCardDetail.NoValidHotels");
    }
    if (requiresRebates && !(el.voucherRebate && el.voucherRebate.length > 0)) {
        reasons.push("VoucherCardDetail.ReasonNoRebates");
    }
    if (!canEmitVoucher) {
        reasons.push("VoucherCardDetail.ReasonNoPermission");
    }

    return reasons;
}

export class CashVoucherCard extends Component {

    render() {
        const { el, removeVoucher, shopVoucher, history, isShoppingCartMode, addVoucher, unselectVoucher, vouchersToSell, icon, displayTitle } = this.props;

        const noValidHotels = !el.creationValidHotels || el.creationValidHotels.length === 0;
        const canEmitVoucher = el.adminRestricted ? CheckAuthorization("adminRestrictedVoucher:emit") : true;

        // single source of truth: the block reasons drive the disabled state
        const blockReasons = getEmitBlockReasons(el, { canEmitVoucher });
        const isEmitVoucherDisabled = blockReasons.length > 0;

        // cart state — not a block reason of the voucher type
        const cartIsFull = vouchersToSell.filter(v => v.voucherTypeId === el.id && v.quantity >= (el.maxQuantity - el.sold)).length > 0;
        const isPlusButtonDisabled = isEmitVoucherDisabled || cartIsFull;
        const hasItemsInCart = vouchersToSell.filter(v => v.voucherTypeId === el.id && v.quantity > 0).length > 0;

        return (
            <Card className="shadow h-100 p-3" body>
                <Row className="h-100">
                    <Col className="d-flex flex-column justify-content-between">
                        <Row className="align-items-start">
                            <Col className="text-ellipsis">
                                <i className={`${icon} mr-2`} /><b title={el.code}> {el.code}</b>
                                {noValidHotels &&
                                    <>
                                        <i id={`noHotels${el.id}`} className="fa-solid fa-triangle-exclamation ml-2 text-warning" style={{ cursor: 'help' }} />
                                        <UncontrolledTooltip placement="top" target={`noHotels${el.id}`}>
                                            <FormattedMessage id="VoucherCardDetail.NoValidHotels" />
                                        </UncontrolledTooltip>
                                    </>
                                }
                            </Col>
                            <Col className="col-4 pl-0 text-right">
                                <VoucherKebabMenu el={el} removeVoucher={removeVoucher} history={history} canEmitVoucher={canEmitVoucher} />
                            </Col>
                        </Row>
                        {displayTitle &&
                            <div className="text-muted text-truncate" style={{ fontSize: '12px' }} title={displayTitle}>
                                {displayTitle}
                            </div>
                        }

                    </Col>
                </Row>

                <div className="my-4" style={{ fontSize: '14px' }} >{el.description}</div>

                <Row className="align-items-center pt-1">
                    <Col>{el.sold} / {el.maxQuantity}</Col>
                    <Col>
                        <Authorization
                            perform="voucherManagement:shop"
                            yes={() => (
                                <div>
                                    {
                                        isShoppingCartMode ?
                                            <span>
                                                <span id={`emitBtn${el.id}`} className="float-right">
                                                    <Button className="btn-host btn-sm" disabled={isPlusButtonDisabled} onClick={_ => addVoucher(el)} style={isPlusButtonDisabled ? { pointerEvents: 'none' } : null}>
                                                        <i className="fa-solid fa-plus" />
                                                    </Button>
                                                </span>
                                                <Button id={`removeVoucher{el.id}`} className="btn-host btn-sm float-right mr-1" disabled={isEmitVoucherDisabled || !hasItemsInCart} onClick={_ => unselectVoucher(el)}>
                                                    <i className="fa-solid fa-minus" />
                                                </Button>
                                                <UncontrolledTooltip placement="bottom" target={`emitBtn${el.id}`}>
                                                    {blockReasons.length > 0
                                                        ? blockReasons.map((r, k) => <div key={k} className="font_size_xxs"><FormattedMessage id={r} /></div>)
                                                        : <FormattedMessage id="VoucherManagement.AddVoucher" />}
                                                </UncontrolledTooltip>
                                                {
                                                    vouchersToSell.filter(v => v.voucherTypeId === el.id).map((v, key) =>
                                                        <Button className="btn-host btn-sm float-right mr-1" key={key}>
                                                            {v.quantity}
                                                        </Button>
                                                    )
                                                }
                                            </span>
                                            :
                                            <span id={`emitBtn${el.id}`} className="float-right">
                                                <Button className="btn-host btn-sm" disabled={isEmitVoucherDisabled} onClick={shopVoucher} style={isEmitVoucherDisabled ? { pointerEvents: 'none' } : null}>
                                                    <i className="fa-solid fa-shopping-basket" />
                                                </Button>
                                                <UncontrolledTooltip placement="bottom" target={`emitBtn${el.id}`}>
                                                    {blockReasons.length > 0
                                                        ? blockReasons.map((r, k) => <div key={k} className="font_size_xxs"><FormattedMessage id={r} /></div>)
                                                        : <FormattedMessage id="VoucherManagement.EmitNewVoucher" />}
                                                </UncontrolledTooltip>
                                            </span>
                                    }
                                </div>
                            )}
                            no={() => <div></div>}
                        />
                    </Col>
                </Row>
            </Card>    
        );
    }
}


export class RebateVoucherCard extends Component {

    render() {
        const { el, removeVoucher, shopVoucher, history, isShoppingCartMode, addVoucher, unselectVoucher, vouchersToSell, icon, displayTitle } = this.props;

        const noValidHotels = !el.creationValidHotels || el.creationValidHotels.length === 0;
        const canEmitVoucher = el.adminRestricted ? CheckAuthorization("adminRestrictedVoucher:emit") : true;

        // single source of truth: block reasons (incl. rebates + permission) drive the disabled state
        const blockReasons = getEmitBlockReasons(el, { canEmitVoucher, requiresRebates: true });
        const isEmitVoucherDisabled = blockReasons.length > 0;

        // cart state — not a block reason of the voucher type
        const cartIsFull = vouchersToSell.filter(v => v.voucherTypeId === el.id && v.quantity >= (el.maxQuantity - el.sold)).length > 0;
        const isPlusButtonDisabled = isEmitVoucherDisabled || cartIsFull;
        const hasItemsInCart = vouchersToSell.filter(v => v.voucherTypeId === el.id && v.quantity > 0).length > 0;

        return (
            <Card className="shadow h-100 p-3" body>
                <Row className="h-100">
                    <Col className="d-flex flex-column justify-content-between">
                        <div>
                            <Row className="align-items-start">
                                <Col className="text-ellipsis">
                                    <i className={`${icon} mr-2`} /><b title={el.code}> {el.code}</b>
                                    {noValidHotels &&
                                        <>
                                            <i id={`noHotels${el.id}`} className="fa-solid fa-triangle-exclamation ml-2 text-warning" style={{ cursor: 'help' }} />
                                            <UncontrolledTooltip placement="top" target={`noHotels${el.id}`}>
                                                <FormattedMessage id="VoucherCardDetail.NoValidHotels" />
                                            </UncontrolledTooltip>
                                        </>
                                    }
                                </Col>
                                <Col className="col-4 pl-0 text-right">
                                    <VoucherKebabMenu el={el} removeVoucher={removeVoucher} history={history} canEmitVoucher={canEmitVoucher} />
                                </Col>
                            </Row>
                            {displayTitle &&
                                <div className="text-muted text-truncate" style={{ fontSize: '12px' }} title={displayTitle}>
                                    {displayTitle}
                                </div>
                            }
                            <div className="my-3" style={{ fontSize: '14px' }}>
                                {el.voucherRebate && el.voucherRebate.length > 0 ?
                                    el.voucherRebate.map((rebate, key) =>
                                        <div key={key}>
                                            - {rebate.code}
                                        </div>
                                    )
                                    :
                                    <span>{el.description}</span>
                                }
                            </div>
                        </div>
                    </Col>
                </Row>

                <Row className="align-items-center pt-1">
                    <Col>{el.sold} / {el.maxQuantity}</Col>
                    <Col>
                        <Authorization
                            perform="voucherManagement:shop"
                            yes={() => (
                                <div>
                                    {
                                        isShoppingCartMode ?
                                            <span>
                                                <span id={`emitBtn${el.id}`} className="float-right">
                                                    <Button className="btn-host btn-sm" disabled={isPlusButtonDisabled} onClick={_ => addVoucher(el)} style={isPlusButtonDisabled ? { pointerEvents: 'none' } : null}>
                                                        <i className="fa-solid fa-plus" />
                                                    </Button>
                                                </span>
                                                <Button id={`removeVoucher{el.id}`} className="btn-host btn-sm float-right mr-1" disabled={isEmitVoucherDisabled || !hasItemsInCart} onClick={_ => unselectVoucher(el)}>
                                                    <i className="fa-solid fa-minus" />
                                                </Button>
                                                <UncontrolledTooltip placement="bottom" target={`emitBtn${el.id}`}>
                                                    {blockReasons.length > 0
                                                        ? blockReasons.map((r, k) => <div key={k} className="font_size_xxs"><FormattedMessage id={r} /></div>)
                                                        : <FormattedMessage id="VoucherManagement.AddVoucher" />}
                                                </UncontrolledTooltip>
                                                {
                                                    vouchersToSell.filter(v => v.voucherTypeId === el.id).map((v, key) =>
                                                        <Button className="btn-host btn-sm float-right mr-1" key={key}>
                                                            {v.quantity}
                                                        </Button>
                                                    )
                                                }
                                            </span>
                                            :
                                            <span id={`emitBtn${el.id}`} className="float-right">
                                                <Button className="btn-host btn-sm" disabled={isEmitVoucherDisabled} onClick={shopVoucher} style={isEmitVoucherDisabled ? { pointerEvents: 'none' } : null}>
                                                    <i className="fa-solid fa-shopping-basket" />
                                                </Button>
                                                <UncontrolledTooltip placement="bottom" target={`emitBtn${el.id}`}>
                                                    {blockReasons.length > 0
                                                        ? blockReasons.map((r, k) => <div key={k} className="font_size_xxs"><FormattedMessage id={r} /></div>)
                                                        : <FormattedMessage id="VoucherManagement.EmitNewVoucher" />}
                                                </UncontrolledTooltip>
                                            </span>
                                    }
                                </div>
                            )}
                            no={() => <div></div>}
                        />
                    </Col>
                </Row>
            </Card>
        );
    }
}


const VoucherKebabMenu = ({ el, removeVoucher, history, canEmitVoucher }) => {
    const canCopy = CheckAuthorization("cardConfigWizard:saveConfig");
    const canEdit = CheckAuthorization("voucherManagement:edit");
    const canDelete = canEmitVoucher && el.sold === 0 && CheckAuthorization("voucherManagement:remove");

    if (!canCopy && !canEdit && !canDelete) return null;

    return (
        <KebabMenu
            copyFunction={canCopy ? _ => history.push({ pathname: '/CardConfigWizard', state: { voucherIdToCopy: el.id } }) : null}
            editFunction={canEdit ? _ => history.push('/CardConfigWizard/' + el.id) : null}
            deleteFunction={canDelete ? _ => removeVoucher() : null}
        />
    );
};